import styled from 'styled-components';
import { AutoTextSize } from 'auto-text-size'
import classNames from 'classnames';
import { useState } from 'react';

const taxOrange = '#F8D7A3';
const taxGreen = '#0f7c47';

const StyledLabelCell = styled.div`
  cursor: pointer;

  .content {
    justify-content: center;
    align-items: center !important;
    background: white;
    color: ${taxGreen};
    width: 100%;
    height: 100%;
    text-align: center;
    line-height: 1.2;
    display: none;
  }

  &.vertical-cell {
    .content {
      writing-mode: vertical-rl;
      transform: rotate(180deg);
    }
  }

  &.debug {
    background: blue;
    z-index: 100;

    > div {
      display: none !important;
    }
  }
`;

const StyledValueCell = styled.div`
  .value {
    height: 100%;
    display: flex;
    color: black;
    align-items: center;
    justify-content: space-between;
    font-family: monospace;

    .segment {
      height: 100%;
      display: flex;
      border: 1px solid ${taxOrange};
      background: white;

      div {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .separator {
        height: 100%;
        background: ${taxOrange};
      }
    }
  }

  &.debug {
    background: yellow;
    z-index: 100;

    > div {
      display: none !important;
    }
  }
`;

const StyledRawValueCell = styled.div`
  display: flex;
  align-items: center !important;
  justify-content: end;
  font-family: monospace;

  &.center {
    justify-content: center;
  }

  &.left {
    justify-content: start;
  }

  &.debug {
    background: red;
    z-index: 100;

    > div {
      display: none !important;
    }
  }
`;

const StyledCoveredCells = styled.div`
  background: rgb(255 255 255 / 80%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: monospace;
  font-size: 18px;
`;

const RawValueCell = ({ value = 0, coords = [10, 10], width, height, sizes, force, debug,
    en, center, left, ...rest }) => {
  const classes = classNames('raw-value-cell', { debug, center, left });
  const topPosition = sizes.height * (coords[0] / 100);
  const leftPosition = sizes.width * (coords[1] / 100);
  const widthInPixels = sizes.width * (width / 100);
  const heightInPixels = sizes.height * (height / 100);
  const style = { position: 'absolute', top: topPosition, left: leftPosition, width:
   widthInPixels, height: heightInPixels };

  // Value to string, with every 1,000 separated by a comma
  const valueString = value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return (
    <StyledRawValueCell className={classes} style={style} {...rest}>
      <AutoTextSize mode={'box'} minFontSizePx={10} maxFontSizePx={16} as={'span'}>{valueString}</AutoTextSize>
    </StyledRawValueCell>
  );
}

const CoveredCells = ({ coords = [10, 10], width, height, sizes, force, debug, children, ...rest }) => {
  const classes = classNames('covered-cell', { debug });
  const top = sizes.height * (coords[0] / 100);
  const left = sizes.width * (coords[1] / 100);
  const widthInPixels = sizes.width * (width / 100);
  const heightInPixels = sizes.height * (height / 100);
  const style = { position: 'absolute', top, left, width:
   widthInPixels, height: heightInPixels };

  return (
    <StyledCoveredCells className={classes} style={style} {...rest}>
      {children}
    </StyledCoveredCells>
  );
}

const LabelVerticalCell = ({ value = 0, coords = [10, 10], width, height, sizes, force, debug, en, ...rest }) => {
  const classes = classNames('label-cell', 'vertical-cell', { debug });
  const top = sizes.height * (coords[0] / 100);
  const left = sizes.width * (coords[1] / 100);
  const widthInPixels = sizes.width * (width / 100);
  const heightInPixels = sizes.height * (height / 100);
  const style = { position: 'absolute', top, left, width:
   widthInPixels, height: heightInPixels };
  const [isDisplayed, setIsDisplayed] = useState(false);

  return (
    <StyledLabelCell className={classes} style={style} {...rest}
      onMouseEnter={() => setIsDisplayed(true)} onMouseLeave={() => setIsDisplayed(false)}>
      {(force || isDisplayed) && <div className="content">
        <AutoTextSize mode={'box'} minFontSizePx={8} maxFontSizePx={16} as={'span'}>{en}</AutoTextSize>
      </div>}
    </StyledLabelCell>
  );
}

const LabelCell = ({ value = 0, coords = [10, 10], width, height, sizes, force, debug, en, ...rest }) => {
  const classes = classNames('label-cell', { debug });
  const top = sizes.height * (coords[0] / 100);
  const left = sizes.width * (coords[1] / 100);
  const widthInPixels = sizes.width * (width / 100);
  const heightInPixels = sizes.height * (height / 100);
  const style = { position: 'absolute', top, left, width:
   widthInPixels, height: heightInPixels };
  const [isDisplayed, setIsDisplayed] = useState(false);

  return (
    <StyledLabelCell className={classes} style={style} {...rest}
      onMouseEnter={() => setIsDisplayed(true)} onMouseLeave={() => setIsDisplayed(false)}>
      {(force || isDisplayed) && <div className="content">
        <AutoTextSize mode={'box'} minFontSizePx={8} maxFontSizePx={16} as={'span'}>{en}</AutoTextSize>
      </div>}
    </StyledLabelCell>
  );
}

const ValueCell = ({ value = 0, coords = [10, 10], width, height, debug, sizes, ...rest }) => {
  const classes = classNames('value-cell', { debug });
  const valueStr = value?.toString().split('').reverse();
  const top = sizes.height * (coords[0] / 100);
  const left = sizes.width * (coords[1] / 100);
  const widthInPixels = sizes.width * (width / 100);
  const heightInPixels = sizes.height * (height / 100);
  const separatorWidth = widthInPixels / 36;
  const cellWidth = widthInPixels / 12;
  const doubleCellWidth = widthInPixels / 5.1;
  const style = { position: 'absolute', top, left, width: widthInPixels, height: heightInPixels };

  return (
    <StyledValueCell className={classes} style={style} {...rest}>
      <div className="value">
        <div className="segment">
          <div style={{ width: doubleCellWidth, justifyContent: 'end', paddingRight: '5%' }}>{valueStr[7]}</div>
        </div>
        <div className="segment">
          <div style={{ width: cellWidth }}>{valueStr[6]}</div>
          <div className="separator" style={{ width: separatorWidth }}></div>
          <div style={{ width: cellWidth }}>{valueStr[5]}</div>
        </div>
        <div className="segment">
          <div style={{ width: cellWidth }}>{valueStr[4]}</div>
        </div>
        <div className="segment">
          <div style={{ width: cellWidth }}>{valueStr[3]}</div>
          <div className="separator" style={{ width: separatorWidth }}></div>
          <div style={{ width: cellWidth }}>{valueStr[2]}</div>
        </div>
        <div className="segment">
          <div style={{ width: cellWidth }}>{valueStr[1]}</div>
        </div>
        <div className="segment">
          <div style={{ width: cellWidth }}>{valueStr[0]}</div>
        </div>
      </div>
    </StyledValueCell>
  );
}

export { CoveredCells, LabelCell, LabelVerticalCell, ValueCell, RawValueCell };